import React, { useCallback } from "react";
import { faDownload } from "@fortawesome/pro-solid-svg-icons";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import download from "downloadjs";
import { convertArrayToCSV } from "convert-array-to-csv";

import { Column } from "react-table";
import _ from "lodash";
import { StdButton } from "./StdButton";
import * as XLSX from "xlsx";
import { useCategoryBreadcrumb } from "../hooks";
import { useLocation } from "react-router-dom";
import styles from "./DownloadButton.module.scss";
import { event as downloadButtonEvent } from "Components/GA/ProviderInfo/DownloadButton";
const JSZip = require("jszip");

const toArrayOfDataArray = (arrayOfDataObjects: unknown[], columns: Column[]) => {
    const columnMap = {};
    arrayOfDataObjects.forEach(d => {
        Object.entries(d).forEach(([column]) => {
            columnMap[column] = 1;
        });
    });

    const _columns = columns.filter(column => columnMap[(column?.accessor ?? "") as string] === 1);

    const arrayOfDataArray = arrayOfDataObjects.map(d => {
        return _columns.map(column => {
            return d[(column?.accessor ?? "") as string] as unknown;
        });
    });

    return {
        arrayOfDataArray,
        columns: _columns?.map(column => (column.Header ?? "") as string)
    };
};

const toArrayOfDataObjects = (arrayOfDataObjects: unknown[], columns: Column[]) => {
    const columnMap = {};
    arrayOfDataObjects.forEach(d => {
        Object.entries(d).forEach(([column]) => {
            columnMap[column] = 1;
        });
    });

    const _columns = columns.filter(column => columnMap[(column?.accessor ?? "") as string] === 1);

    const _arrayOfDataObjects = arrayOfDataObjects.map(d => {
        const newRet = {};
        _columns.forEach(column => {
            // return d[(column?.accessor ?? "") as string] as unknown;

            newRet[(column?.Header ?? "") as string] = d[(column?.accessor ?? "") as string] as unknown;
        });

        return newRet;
    });

    return {
        arrayOfDataObjects: _arrayOfDataObjects,
        columns: _columns?.map(column => (column.Header ?? "") as string)
    };
};

const toCSVData = (arrayOfDataObjects: unknown[], columns: Column[]) => {
    const { arrayOfDataArray: data, columns: _columns } = toArrayOfDataArray(arrayOfDataObjects, columns);
    const csvData = convertArrayToCSV(data, {
        header: _columns
    });

    return csvData;
};

export interface DownloadButtonProps {
    disabled?: boolean;
    fileName: string;
    dataObjects: {}[];
    columns: Column[];
    className?: string;
    buttonLabel?: string;
}

export const DownloadButton: React.FC<DownloadButtonProps> = ({
    disabled = false,
    fileName,
    dataObjects = [],
    columns = [],
    className = "",
    buttonLabel = "Download this data"
}) => {
    const { pathname } = useLocation();
    const { breadcrumb } = useCategoryBreadcrumb();

    const onClick = useCallback(() => {
        const csvData = toCSVData(dataObjects, columns);
        download(csvData, fileName, "text/plain");
        downloadButtonEvent(pathname, breadcrumb);
    }, [pathname, breadcrumb, dataObjects, columns, fileName]);

    return (
        <>
            <StdButton
                onClick={onClick}
                className={`btn btn-std ${styles.downloadButton} ${className}`}
                disabled={disabled}
            >
                <FontAwesomeIcon icon={faDownload} className="mr-2" />
                {buttonLabel}
            </StdButton>
        </>
    );
};

export interface DownloadButtonForFilesProps {
    disabled?: boolean;

    providerNames: string;
    files: {
        fileName: string;
        dataObjects: {}[];
        columns: Column[];
    }[];

    fileName: string;
    className?: string;
    buttonLabel?: string;
}

export const DownloadButtonForFiles: React.FC<DownloadButtonForFilesProps> = ({
    providerNames,
    disabled = false,
    files,
    className = "",
    fileName,
    buttonLabel = "Download this data"
}) => {
    const { pathname } = useLocation();
    const { breadcrumb } = useCategoryBreadcrumb();

    // const onClick = useCallback(() => {
    //     const csvData = toCSVData(dataObjects, columns);
    //     download(csvData, fileName, "text/plain");
    //     downloadButtonEvent(pathname, breadcrumb);
    // }, [pathname, breadcrumb, dataObjects, columns, fileName]);

    const onClick = useCallback(() => {
        if (files?.length === 1) {
            const file = files?.[0];
            const csvData = toCSVData(file?.dataObjects, file?.columns);

            download(
                csvData,
                _.join(
                    [providerNames, files?.[0]?.fileName].filter(d => d != null && d.trim() !== ""),
                    " - "
                ),
                "text/plain"
            );
            return;
        }

        const zip = new JSZip();

        files.forEach(file => {
            if (file) {
                const csvData = toCSVData(file?.dataObjects, file?.columns);
                zip.file(file.fileName, csvData);
            }
        });

        zip.generateAsync({ type: "blob" }).then(function (content) {
            // see FileSaver.js
            download(content, `${fileName}.zip`);
        });

        downloadButtonEvent(pathname, breadcrumb);
    }, [pathname, breadcrumb, files, providerNames, fileName]);

    if (!files?.length) return null;

    return (
        <>
            <button
                onClick={onClick}
                className={`btn btn-std ${styles.downloadButton} ${className}`}
                disabled={disabled}
            >
                <FontAwesomeIcon icon={faDownload} className={`${styles.downloadIcon}`} />
                {buttonLabel}
            </button>
        </>
    );
};

export interface DownloadExcelFileForCSVSProps {
    disabled?: boolean;

    // providerNames: string;
    files: {
        fileName: string;
        dataObjects: {}[];
        columns: Column[];
    }[];

    fileName: string;
    className?: string;
    buttonLabel?: string;
}

export const DownloadExcelFileForCSVS: React.FC<DownloadExcelFileForCSVSProps> = ({
    disabled = false,
    // providerNames,
    files,
    fileName,
    className,
    buttonLabel
}) => {
    const { pathname } = useLocation();
    const { breadcrumb } = useCategoryBreadcrumb();

    const onClick = useCallback(() => {
        // if (files?.length === 1) {
        // const file = files?.[0];
        // const csvData = toCSVData(file?.dataObjects, file?.columns);

        // download(csvData, `${fileName}.csv`, "text/plain");
        // return;
        // }

        if (!files?.length) {
            return;
        }

        const wb = XLSX.utils.book_new();

        files.forEach(file => {
            const { arrayOfDataObjects } = toArrayOfDataObjects(file.dataObjects, file.columns);
            const wsObjects = XLSX.utils.json_to_sheet(arrayOfDataObjects);
            XLSX.utils.book_append_sheet(wb, wsObjects, file.fileName);
        });

        const fileType = "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet;charset=UTF-8";
        const excelBuffer = XLSX.write(wb, { bookType: "xlsx", type: "array" });
        const blob = new Blob([excelBuffer], { type: fileType });
        download(blob, `${fileName}.xlsx`);

        downloadButtonEvent(pathname, breadcrumb);
    }, [pathname, breadcrumb, files, fileName]);

    if (!files?.length) return null;

    return (
        <>
            <button
                onClick={onClick}
                className={`btn btn-std ${styles.downloadButton} ${className}`}
                disabled={disabled}
            >
                <FontAwesomeIcon icon={faDownload} className={`${styles.downloadIcon}`} />
                {buttonLabel}
            </button>
        </>
    );
};

export const DownloadExcelFileForCSVSDemo: React.FC<{}> = () => {
    return (
        <DownloadExcelFileForCSVS
            fileName={"testxlsx"}
            files={[
                {
                    columns: [
                        {
                            accessor: "name"
                        },
                        {
                            accessor: "age"
                        }
                    ],
                    dataObjects: [
                        {
                            name: "aaaa",
                            age: 32
                        }
                    ],
                    fileName: "name"
                },
                {
                    columns: [
                        {
                            accessor: "name"
                        },
                        {
                            accessor: "age"
                        }
                    ],
                    dataObjects: [
                        {
                            name: "aaaa",
                            age: 32
                        },
                        {
                            name: "blah",
                            age: 32
                        }
                    ],
                    fileName: "nameblah"
                }
            ]}
            buttonLabel={"Download excel"}
            disabled={false}
        />
    );
};
